import React, { useEffect, useRef, useState } from 'react';

// Lightweight loader for Google Maps Places API script with PlaceAutocompleteElement
function useGooglePlaces(apiKey) {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const checkGoogleMaps = () => {
      return (
        window.google &&
        window.google.maps &&
        window.google.maps.places &&
        window.google.maps.places.Autocomplete
      );
    };

    if (typeof window !== 'undefined' && checkGoogleMaps()) {
      setLoaded(true);
      return;
    }

    if (!apiKey) return;

    const existing = document.querySelector('script[data-role="gmaps-places"]');
    if (existing) {
      // Check if already loaded
      if (checkGoogleMaps()) {
        setLoaded(true);
        return;
      }
      // Wait for load event
      existing.addEventListener('load', () => {
        // Add a small delay to ensure API is fully initialized
        setTimeout(() => {
          if (checkGoogleMaps()) {
            setLoaded(true);
          }
        }, 100);
      });
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&v=weekly&libraries=places&language=en-AU&region=AU`;
    script.async = true;
    script.defer = true;
    script.setAttribute('data-role', 'gmaps-places');
    script.onload = () => {
      // Add a small delay to ensure API is fully initialized
      setTimeout(() => {
        if (checkGoogleMaps()) {
          setLoaded(true);
        } else {
          // Retry a few times if not ready
          let retries = 0;
          const retryInterval = setInterval(() => {
            retries++;
            if (checkGoogleMaps()) {
              setLoaded(true);
              clearInterval(retryInterval);
            } else if (retries > 10) {
              console.error('Google Maps API failed to initialize after 10 retries');
              clearInterval(retryInterval);
            }
          }, 200);
        }
      }, 100);
    };
    document.head.appendChild(script);
    return () => {
      // do not remove script to allow caching across pages
    };
  }, [apiKey]);
  return loaded;
}

// Helper to extract structured address components from Google Place result (AU oriented)
function parsePlace(place) {
  const component = (type) => {
    const c = place.address_components?.find((c) => c.types.includes(type));
    return c ? c.long_name : '';
  };
  const short = (type) => {
    const c = place.address_components?.find((c) => c.types.includes(type));
    return c ? c.short_name : '';
  };
  return {
    line1: `${component('street_number')} ${component('route')}`.trim(),
    line2: '',
    city: component('locality') || component('postal_town') || component('sublocality') || '',
    state: short('administrative_area_level_1'),
    postal_code: component('postal_code'),
    country: short('country'),
    formatted: place.formatted_address || '',
    placeId: place.place_id || '',
    lat: place.geometry?.location?.lat ? place.geometry.location.lat() : undefined,
    lng: place.geometry?.location?.lng ? place.geometry.location.lng() : undefined,
  };
}

// Australian states for dropdown
const AU_STATES = [
  { code: 'NSW', name: 'New South Wales' },
  { code: 'VIC', name: 'Victoria' },
  { code: 'QLD', name: 'Queensland' },
  { code: 'WA', name: 'Western Australia' },
  { code: 'SA', name: 'South Australia' },
  { code: 'TAS', name: 'Tasmania' },
  { code: 'ACT', name: 'Australian Capital Territory' },
  { code: 'NT', name: 'Northern Territory' },
];

export default function AddressAutocomplete({
  value,
  onChange,
  onValidChange,
  country = 'AU',
  className = '',
}) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const loaded = useGooglePlaces(apiKey);
  const autocompleteRef = useRef(null);
  const isUpdatingFromProps = useRef(false);
  const [address, setAddress] = useState({
    line1: value?.line1 || '',
    line2: value?.line2 || '',
    city: value?.city || '',
    state: value?.state || '',
    postal_code: value?.postal_code || '',
    country: value?.country || 'AU',
    formatted: value?.formatted || '',
    placeId: value?.placeId || '',
    lat: value?.lat,
    lng: value?.lng,
  });
  const [isValid, setIsValid] = useState(false);

  // Validate address
  const validateAddress = (addr) => {
    const valid = !!(
      addr.line1?.trim() &&
      addr.city?.trim() &&
      addr.state?.trim() &&
      addr.postal_code?.trim() &&
      addr.country === 'AU'
    );
    return valid;
  };

  // Sync internal state when value prop changes
  useEffect(() => {
    if (!value) return;

    const nextAddress = {
      line1: value.line1 || '',
      line2: value.line2 || '',
      city: value.city || '',
      state: value.state || '',
      postal_code: value.postal_code || '',
      country: value.country || 'AU',
      formatted: value.formatted || '',
      placeId: value.placeId || '',
      lat: value.lat,
      lng: value.lng,
    };

    const differs = (
      (address.line1 || '') !== nextAddress.line1 ||
      (address.line2 || '') !== nextAddress.line2 ||
      (address.city || '') !== nextAddress.city ||
      (address.state || '') !== nextAddress.state ||
      (address.postal_code || '') !== nextAddress.postal_code ||
      (address.country || '') !== nextAddress.country ||
      (address.formatted || '') !== nextAddress.formatted ||
      (address.placeId || '') !== nextAddress.placeId ||
      (address.lat ?? '') !== (nextAddress.lat ?? '') ||
      (address.lng ?? '') !== (nextAddress.lng ?? '')
    );

    if (differs) {
      isUpdatingFromProps.current = true;
      setAddress(nextAddress);
    }
  }, [value]);

  // Update validation when address changes
  useEffect(() => {
    const valid = validateAddress(address);
    setIsValid(valid);
    onValidChange && onValidChange(valid);

    // Only call onChange if this is not an update from props
    if (isUpdatingFromProps.current) {
      // Clear the flag after handling the prop-driven update
      isUpdatingFromProps.current = false;
      return;
    }

    onChange && onChange(address);
  }, [address]);

  // Initialize autocomplete when loaded
  useEffect(() => {
    if (!loaded || !window.google?.maps?.places?.Autocomplete) return;

    // Small delay to ensure the input is rendered
    const timer = setTimeout(() => {
      const input = document.getElementById('address-autocomplete-input');
      console.log('AddressAutocomplete: Initializing autocomplete', {
        input: !!input,
        hasAutocomplete: !!autocompleteRef.current,
        loaded,
      });

      if (!input || autocompleteRef.current) return;

      const options = {
        fields: ['address_components', 'formatted_address', 'geometry', 'place_id'],
        types: ['address'],
        componentRestrictions: { country: 'AU' },
      };

      autocompleteRef.current = new window.google.maps.places.Autocomplete(input, options);
      console.log('AddressAutocomplete: Autocomplete initialized successfully');

      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();
        if (place.address_components) {
          const parsed = parsePlace(place);
          setAddress((prev) => ({
            ...prev,
            ...parsed,
            country: 'AU', // Force AU
          }));
        }
      });
    }, 100);

    return () => {
      clearTimeout(timer);
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, [loaded]);

  const handleFieldChange = (field, value) => {
    setAddress((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <div className={`address-form ${className}`}>
      <div className="autocomplete-section">
        <label htmlFor="address-autocomplete-input" className="field-label">
          Search for your address
        </label>
        <input
          id="address-autocomplete-input"
          type="text"
          className="address-input"
          placeholder="Start typing your address..."
          autoComplete="off"
        />
        {!apiKey && (
          <div className="address-error">
            Google Maps API key not configured. Please set REACT_APP_GOOGLE_MAPS_API_KEY.
          </div>
        )}
        {apiKey && !loaded && <div className="address-hint">Loading address suggestions...</div>}
      </div>

      <div className="address-form-header">
        <h4>Shipping Address</h4>
      </div>

      <div className="address-fields">
        <div className="field-row">
          <div className="field-group">
            <label htmlFor="line1" className="field-label">
              Address Line 1 *
            </label>
            <input
              id="line1"
              type="text"
              className="address-input"
              value={address.line1}
              onChange={(e) => handleFieldChange('line1', e.target.value)}
              placeholder="Street number and name"
              required
            />
          </div>
        </div>

        <div className="field-row">
          <div className="field-group">
            <label htmlFor="line2" className="field-label">
              Address Line 2
            </label>
            <input
              id="line2"
              type="text"
              className="address-input"
              value={address.line2}
              onChange={(e) => handleFieldChange('line2', e.target.value)}
              placeholder="Apartment, unit, etc. (optional)"
            />
          </div>
        </div>

        <div className="field-row">
          <div className="field-group">
            <label htmlFor="city" className="field-label">
              Suburb/City *
            </label>
            <input
              id="city"
              type="text"
              className="address-input"
              value={address.city}
              onChange={(e) => handleFieldChange('city', e.target.value)}
              placeholder="Suburb or city"
              required
            />
          </div>
        </div>

        <div className="field-row field-row-two-cols">
          <div className="field-group">
            <label htmlFor="state" className="field-label">
              State *
            </label>
            <select
              id="state"
              className="address-select"
              value={address.state}
              onChange={(e) => handleFieldChange('state', e.target.value)}
              required
            >
              <option value="">Select State</option>
              {AU_STATES.map((state) => (
                <option key={state.code} value={state.code}>
                  {state.name}
                </option>
              ))}
            </select>
          </div>

          <div className="field-group">
            <label htmlFor="postal_code" className="field-label">
              Postcode *
            </label>
            <input
              id="postal_code"
              type="text"
              className="address-input"
              value={address.postal_code}
              onChange={(e) => handleFieldChange('postal_code', e.target.value)}
              placeholder="1234"
              pattern="[0-9]{4}"
              maxLength="4"
              required
            />
          </div>
        </div>

        <div className="field-row">
          <div className="field-group">
            <label htmlFor="country" className="field-label">
              Country
            </label>
            <input id="country" type="text" className="address-input" value="Australia" disabled />
          </div>
        </div>
      </div>

      {!isValid && (address.line1 || address.city || address.state || address.postal_code) && (
        <div className="address-hint">Please fill in all required fields to continue.</div>
      )}
    </div>
  );
}
